import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, X, CheckCheck, Camera, DollarSign, FileText, Shield, Wrench, User, AlertCircle, MessageSquare, Clock, Filter } from 'lucide-react';

interface Notification {
  id: string;
  type: 'quotation' | 'invoice' | 'payment' | 'warranty' | 'job' | 'alert' | 'system';
  title: string;
  message: string;
  time: string;
  read: boolean;
  audience: ('owner' | 'technician' | 'customer')[];
  priority: 'high' | 'medium' | 'low';
}

const mockNotifications: Notification[] = [
  { id: '1', type: 'payment', title: 'Payment Received', message: 'LKR 485,000 payment confirmed from AIA Insurance (INV-047)', time: '2 min ago', read: false, audience: ['owner'], priority: 'high' },
  { id: '2', type: 'job', title: 'Technician Arrived', message: 'Kamal Perera has checked in at AIA Insurance site', time: '5 min ago', read: false, audience: ['customer', 'owner'], priority: 'medium' },
  { id: '3', type: 'quotation', title: 'Quotation Ready', message: 'QT-2026-089 for Hotel Reef Network Setup is ready for approval', time: '15 min ago', read: false, audience: ['customer', 'owner'], priority: 'medium' },
  { id: '4', type: 'warranty', title: 'Warranty Activated', message: 'Warranty #WR-2026-045 activated for Sampath Bank CCTV installation', time: '1 hr ago', read: true, audience: ['customer', 'owner'], priority: 'low' },
  { id: '5', type: 'alert', title: 'Low Stock Alert', message: 'Hikvision DS-2CD2T47G2-L is running low. Only 3 units remaining.', time: '2 hrs ago', read: true, audience: ['owner'], priority: 'high' },
  { id: '6', type: 'job', title: 'Job Completed', message: 'Kamal Perera completed Dialog Axiata Starlink installation', time: '3 hrs ago', read: true, audience: ['owner', 'customer'], priority: 'medium' },
  { id: '7', type: 'warranty', title: 'Warranty Expiring Soon', message: 'Dahua NVR warranty for Lanka Hospitals expires in 30 days', time: '5 hrs ago', read: true, audience: ['customer', 'owner'], priority: 'medium' },
  { id: '8', type: 'invoice', title: 'Invoice Generated', message: 'INV-2026-051 for LKR 85,000 sent to Hotel Reef', time: '1 day ago', read: true, audience: ['customer', 'owner'], priority: 'low' },
  { id: '9', type: 'system', title: 'Daily Backup Complete', message: 'System backup completed successfully. All data secured.', time: '1 day ago', read: true, audience: ['owner'], priority: 'low' },
  { id: '10', type: 'job', title: 'New Booking Assigned', message: 'Nimal Silva assigned to Hotel Reef Network Setup on Jul 12', time: '2 days ago', read: true, audience: ['technician'], priority: 'medium' },
];

const typeConfig: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  quotation: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100' },
  invoice: { icon: DollarSign, color: 'text-green-600', bg: 'bg-green-100' },
  payment: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  warranty: { icon: Shield, color: 'text-violet-600', bg: 'bg-violet-100' },
  job: { icon: Wrench, color: 'text-[#658C53]', bg: 'bg-[#E8F2E3]' },
  alert: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100' },
  system: { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-100' },
};

const priorityColors: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-300',
};

const audienceLabels: Record<string, string> = {
  owner: 'Owner',
  technician: 'Technician',
  customer: 'Customer',
};
const audienceColors: Record<string, string> = {
  owner: 'bg-violet-100 text-violet-700',
  technician: 'bg-blue-100 text-blue-700',
  customer: 'bg-green-100 text-green-700',
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [filter, setFilter] = useState<'all' | 'unread' | 'owner' | 'technician' | 'customer'>('all');

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'owner' || filter === 'technician' || filter === 'customer') return n.audience.includes(filter);
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  const markRead = (id: string) => setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const deleteNotif = (id: string) => setNotifications(prev => prev.filter(n => n.id !== id));

  const whatsappTemplates = [
    { title: 'Quotation Ready', template: 'Dear {name}, Your quotation QT-{number} for LKR {amount} is ready. View: {link}', trigger: 'On quotation approval' },
    { title: 'Invoice Sent', template: 'Dear {name}, Invoice INV-{number} for LKR {amount} has been sent. Pay online: {link}', trigger: 'On invoice creation' },
    { title: 'Payment Reminder', template: 'Dear {name}, Your invoice INV-{number} of LKR {amount} is due on {date}. Pay: {link}', trigger: '3 days before due' },
    { title: 'Technician Arriving', template: 'Dear {name}, Our technician {tech_name} is on the way. ETA: {eta}. Track: {link}', trigger: 'On job check-out' },
    { title: 'Warranty Certificate', template: 'Dear {name}, Your warranty certificate WR-{number} is ready. Download: {link}', trigger: 'On job completion' },
    { title: 'Warranty Expiry', template: 'Dear {name}, Your warranty for {product} expires in 30 days. Renew: {link}', trigger: '30 days before expiry' },
  ];

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-200">
              <Bell className="w-5 h-5 text-white" />
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
            <p className="text-xs text-gray-500">Push notifications · WhatsApp automation · Email alerts</p>
          </div>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="flex items-center gap-2 text-xs text-gray-600 bg-white border border-gray-200 px-3 py-2 rounded-xl hover:bg-gray-50 transition-colors">
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notifications List */}
        <div className="lg:col-span-2">
          {/* Filters */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto">
            {(['all', 'unread', 'owner', 'technician', 'customer'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${filter === f ? 'bg-rose-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              >
                {f === 'all' ? 'All' : f === 'unread' ? `Unread (${unreadCount})` : audienceLabels[f]}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <AnimatePresence>
              {filtered.map(notif => {
                const config = typeConfig[notif.type];
                const Icon = config.icon;
                return (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    className={`flex items-start gap-3 p-4 rounded-2xl border transition-all hover:shadow-sm ${notif.read ? 'bg-white border-gray-200' : 'bg-blue-50/50 border-blue-200'}`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className={`text-xs font-semibold ${notif.read ? 'text-gray-800' : 'text-gray-900'}`}>{notif.title}</h3>
                            <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${priorityColors[notif.priority]}`} />
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{notif.message}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[10px] text-gray-400 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{notif.time}</span>
                            {notif.audience.map(a => (
                              <span key={a} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${audienceColors[a]}`}>{a}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!notif.read && (
                            <button onClick={() => markRead(notif.id)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                              <Check className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                          )}
                          <button onClick={() => deleteNotif(notif.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                            <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {filtered.length === 0 && (
              <div className="text-center py-10 text-sm text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                No notifications
              </div>
            )}
          </div>
        </div>

        {/* WhatsApp Automation */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-xl bg-green-500 flex items-center justify-center">
                <MessageSquare className="w-3.5 h-3.5 text-white" />
              </div>
              <h2 className="text-sm font-semibold text-gray-900">WhatsApp Automation</h2>
              <span className="ml-auto text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">ACTIVE</span>
            </div>
            <div className="space-y-3">
              {whatsappTemplates.map((tmpl, i) => (
                <div key={i} className="border border-gray-100 rounded-xl p-3 hover:border-green-200 hover:bg-green-50/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-900">{tmpl.title}</span>
                    <div className="w-8 h-4 bg-green-500 rounded-full flex items-center justify-end pr-0.5">
                      <div className="w-3 h-3 bg-white rounded-full shadow" />
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">Trigger: {tmpl.trigger}</p>
                  <p className="text-[10px] text-gray-500 mt-1 font-mono bg-gray-50 rounded-lg p-1.5 truncate">{tmpl.template}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Push Notification Stats */}
          <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl p-5 text-white">
            <h3 className="text-sm font-semibold mb-3">This Month's Notifications</h3>
            {[['WhatsApp Sent', '284'], ['Email Sent', '156'], ['Push Sent', '442'], ['Opened Rate', '78%']].map(([label, val]) => (
              <div key={label as string} className="flex items-center justify-between py-1.5 border-b border-white/10 last:border-0">
                <span className="text-xs opacity-80">{label}</span>
                <span className="text-sm font-bold">{val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
